import { addDays } from 'date-fns';
import { TimeInterval, Timezone } from '@/features/report-snapshots/api/schema';
import type { I18n } from '@lingui/core';

type TimeStamp = number;
type TimeStampLike = number | string;

const getDateOptions = (interval: TimeInterval): Intl.DateTimeFormatOptions => {
  switch (interval) {
    case 'hour':
      return { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true };
    case 'day':
    case 'week':
      return { month: 'short', day: 'numeric' };
    case 'month':
      return { month: 'short' };
    case 'day_of_week':
      return { weekday: 'short' };
    case 'hour_of_day':
      return { hour: 'numeric', minute: '2-digit', hour12: true };
    default:
      throw new Error(`unsupported interval`);
  }
};

const getPeriodRange = (start: Date, interval: TimeInterval): Date[] => {
  switch (interval) {
    case 'week':
      return [start, addDays(start, 6)];
    case 'day_of_week':
      return [start];
    case 'hour_of_day':
      return [start];
    case 'day':
    case 'month':
    case 'hour':
      return [start];
    default:
      throw new Error(`unsupported interval`);
  }
};

export function formatDateLabel(value: TimeStamp) {
  const date = new Date(value);
  const month = date.toLocaleString('en', { month: 'short' });
  const day = date.toLocaleString('en', { day: 'numeric' });
  return `${month} ${day}`;
}

export function formatTimestamp(
  interval: TimeInterval,
  value: TimeStampLike,
  timezone: Timezone,
  i18n: I18n,
) {
  const dateOptions = getDateOptions(interval);
  const timestampValue = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(timestampValue)) {
    return '\u2014';
  }

  const baseDate = new Date(timestampValue);
  if (!Number.isFinite(baseDate.getTime())) {
    return '\u2014';
  }

  let start: Date;
  try {
    start = new Date(baseDate.toLocaleString('en', { timeZone: timezone.ianaName }));
  } catch {
    return '\u2014';
  }

  if (!Number.isFinite(start.getTime())) {
    return '\u2014';
  }

  return getPeriodRange(start, interval)
    .map((date) => i18n.date(date, dateOptions))
    .join(' - ')
    .replace(/AM/g, 'am')
    .replace(/PM/g, 'pm');
}

export function formatDayOfWeekShort(value: number, i18n: I18n) {
  if (value < 1 || value > 7) return '';
  const baseMonday = new Date(Date.UTC(1970, 0, 5));
  const date = new Date(baseMonday);
  date.setUTCDate(baseMonday.getUTCDate() + (value - 1));
  return date.toLocaleDateString(i18n.locale, { weekday: 'short' });
}
